import { useState } from 'react'

import type { Evento, PasoPorEstacion } from '@/lib/supabase/tipos-bd'
import { duracion } from '@/modules/tablero/duracion'
import { fechaHora } from '@/shared/utilidades/formato'

import { useEventosDeEtapa } from './useEventosDeEtapa'
import estilos from './DesgloseFases.module.css'

/** Cómo se titula cada tipo de evento dentro de la etapa. */
const TITULO = {
  creacion: 'Se creó el documento',
  cambio_estado: 'Cambió de estado',
  aprobacion: 'Aprobado',
  rechazo: 'Rechazado',
  asignacion: 'Se asignó responsable',
  nota: 'Nota',
} as const

/*
 * UN COLOR POR FASE, Y SU NÚMERO.
 *
 * Johan: «sigue sin verse el orden; mejor una fase de cada color». Con dos
 * tonos —morado para el trabajo, naranja para las compuertas— la cinta era una
 * raya morada con dos manchas, y no había forma de saber qué tramo era cuál
 * sin pasar el ratón. Ahora cada fase tiene su color, el mismo en la cinta y
 * en la lista, y lleva su número del recorrido (1 a 9) para leer el orden sin
 * contar.
 *
 * Los colores avanzan por la rueda en el orden del recorrido —azules al
 * principio, verdes en la obra, cálidos al cerrar— y tienen todos la misma
 * fuerza, para que ninguna fase parezca más importante por su color.
 */
const COLOR_DE_FASE: Record<number, string> = {
  1: '#5b6bd6', // Orden de servicio
  2: '#2f8fd8', // Cotización
  3: '#17a2b8', // Aprobación
  4: '#7c5bd6', // Programación
  5: '#2e9e6a', // Ejecución
  6: '#8aa62a', // Precierre
  7: '#d89a1c', // Cierre
  8: '#e0663a', // Acta y firma
  9: '#c94f8a', // Servicio finalizado
}

const colorDe = (estacion: number) => COLOR_DE_FASE[estacion] ?? '#8a8fa3'

/**
 * Las horas que el servicio pasó en cada fase, y qué pasó dentro.
 *
 * Es lo que queda constatado en el archivo: no cuánto se demoró en total, sino
 * dónde se fue el tiempo. Una obra de tres semanas que estuvo dos esperando
 * una firma no es una obra lenta, es una firma lenta.
 *
 * Cada fase se despliega y muestra los hechos de esa franja, sacados de la
 * bitácora de los tres documentos (orden de servicio, cotización y orden de
 * trabajo). Así se ve, en la misma línea, lo que hizo cada módulo.
 */
export function DesgloseFases({
  estaciones,
  numeros,
}: {
  estaciones: PasoPorEstacion[]
  numeros: (string | null | undefined)[]
}) {
  const [abierta, setAbierta] = useState<number | null>(null)
  const conHoras = estaciones.filter((e) => e.horas !== null)
  const total = conHoras.reduce((suma, e) => suma + (e.horas ?? 0), 0)

  if (conHoras.length === 0) {
    return <p className={estilos.vacio}>Sin pasos registrados.</p>
  }

  return (
    <div className={estilos.zona}>
      {/* Una sola barra partida: se ve de un golpe qué fase se comió el tiempo. */}
      <div className={estilos.cinta}>
        {conHoras.map((estacion) => (
          <span
            key={estacion.estacion}
            className={estilos.tramo}
            style={{
              width: `${total > 0 ? ((estacion.horas ?? 0) / total) * 100 : 0}%`,
              background: colorDe(estacion.estacion),
            }}
            title={`${estacion.estacion}. ${estacion.nombre} · ${duracion(estacion.horas)}`}
          />
        ))}
      </div>

      <ul className={estilos.lista}>
        {conHoras.map((estacion) => {
          const desplegada = abierta === estacion.estacion
          return (
            <li key={estacion.estacion} className={estilos.fila}>
              <button
                type="button"
                className={estilos.filaBoton}
                onClick={() => setAbierta(desplegada ? null : estacion.estacion)}
                aria-expanded={desplegada}
              >
                <span
                  className={estilos.punto}
                  style={{ background: colorDe(estacion.estacion) }}
                  aria-hidden="true"
                >
                  {estacion.estacion}
                </span>
                <span className={estilos.nombre}>{estacion.nombre}</span>
                <span className={estilos.horas}>{duracion(estacion.horas)}</span>
                <span className={estilos.parte}>
                  {total > 0 ? `${Math.round(((estacion.horas ?? 0) / total) * 100)} %` : '—'}
                </span>
                <span className={estilos.chevron} aria-hidden="true">
                  {desplegada ? '−' : '+'}
                </span>
              </button>

              {desplegada && (
                <EtapaPorDentro
                  numeros={numeros}
                  entrada={estacion.entrada ?? null}
                  salida={estacion.salida ?? null}
                />
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function EtapaPorDentro({
  numeros,
  entrada,
  salida,
}: {
  numeros: (string | null | undefined)[]
  entrada: string | null
  salida: string | null
}) {
  const { eventos, cargando } = useEventosDeEtapa(numeros, entrada, salida, true)

  if (cargando) return <p className={estilos.sinEventos}>Abriendo la etapa…</p>
  if (eventos.length === 0) {
    return <p className={estilos.sinEventos}>Nada quedó registrado en esta etapa.</p>
  }

  return (
    <ul className={estilos.detalleEtapa}>
      {eventos.map((e: Evento) => (
        <li key={e.id} className={estilos.evento}>
          <span className={estilos.eventoHora}>{fechaHora(e.ocurrido_en)}</span>
          <span className={estilos.eventoTexto}>
            <strong>{TITULO[e.tipo as keyof typeof TITULO] ?? 'Movimiento'}</strong>
            {e.documento_numero && <span className={estilos.eventoDoc}>{e.documento_numero}</span>}
            {e.notas && <span className={estilos.eventoNota}>{e.notas}</span>}
          </span>
          <span className={estilos.eventoActor}>{e.actor_nombre}</span>
        </li>
      ))}
    </ul>
  )
}
